import { useEffect } from "react";
import { useRoute, useLocation, Link } from "wouter";
import { ArrowLeft, Check, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LEVEL_FIFTEEN_PRODUCTS } from "@/components/level-fifteen-view";
import NotFound from "@/pages/not-found";

export default function MarketPlaceProductPage() {
  const [, params] = useRoute("/marketplace/:productId");
  const [, navigate] = useLocation();
  const productId = params?.productId;
  const product = LEVEL_FIFTEEN_PRODUCTS.find((p) => p.id === productId);

  // Super Schema lives on the dedicated /schema page now — there is only one
  // URL for it. Bounce any legacy /marketplace/super-schema visits over.
  useEffect(() => {
    if (productId === "super-schema") navigate("/schema", { replace: true });
  }, [productId, navigate]);

  if (productId === "super-schema") return null;
  if (!product) return <NotFound />;

  const Icon = product.icon;
  const isGoal = !!product.goal;

  const accentRing: Record<string, string> = {
    orange: "from-orange-500/20 to-amber-500/10 text-orange-400 border-orange-500/30",
    teal: "from-teal-500/20 to-cyan-500/10 text-teal-300 border-teal-500/30",
    purple: "from-purple-500/20 to-fuchsia-500/10 text-purple-400 border-purple-500/30",
    amber: "from-amber-500/20 to-yellow-500/10 text-amber-300 border-amber-500/30",
    blue: "from-blue-500/20 to-indigo-500/10 text-blue-400 border-blue-500/30",
    emerald: "from-emerald-500/20 to-green-500/10 text-emerald-300 border-emerald-500/30",
  };
  const accent = accentRing[product.accent] ?? accentRing.amber;

  return (
    <div
      className="container max-w-4xl py-8 space-y-6"
      data-testid={`container-marketplace-product-${product.id}`}
    >
      <Link
        to="/total-domination"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        data-testid="link-back-total-domination-top"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Total Domination
      </Link>

      <Card className={`border-2 bg-gradient-to-br ${accent}`} data-testid="card-product-detail">
        <CardContent className="p-8 space-y-6">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center bg-background/40 ${accent}`}>
              <Icon className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] opacity-80 mb-1">
                {product.tagline}
              </div>
              <h1 className="text-3xl font-bold text-foreground" data-testid="text-product-title">
                {product.title}
              </h1>
            </div>
            <div className="text-right shrink-0">
              <div className="text-2xl font-bold text-foreground" data-testid="text-product-price">
                {product.priceLabel}
              </div>
              {isGoal && (
                <Badge variant="secondary" className="mt-2 text-[10px]">
                  Goal-based
                </Badge>
              )}
            </div>
          </div>

          <p className="text-base leading-relaxed text-foreground/85" data-testid="text-product-dominates">
            {product.dominates}
          </p>

          {isGoal && product.goal && (
            <div className="rounded-lg border border-foreground/10 bg-background/30 p-4 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-widest text-foreground/60">
                Monthly target
              </div>
              <div className="text-lg font-semibold">
                {product.goal.target} {product.goal.unit} <span className="text-sm font-normal text-foreground/60">/ {product.goal.periodLabel}</span>
              </div>
              {product.goal.fallback && (
                <div className="pt-3 border-t border-foreground/10 text-sm text-foreground/75">
                  <div className="font-semibold">{product.goal.fallback.title}</div>
                  <div className="text-xs opacity-80 mt-1">{product.goal.fallback.description}</div>
                  <div className="text-xs font-semibold mt-2">{product.goal.fallback.priceLabel}</div>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button
              size="lg"
              className="bg-brand-orange hover:bg-brand-orange/90 text-white shadow-lg shadow-brand-orange/20"
              data-testid="button-activate-product"
              onClick={() => {
                // TODO: wire to Whop checkout per product. For now, send users
                // back to Total Domination so they can pick another tile.
                window.location.href = "/total-domination";
              }}
            >
              <Check className="w-4 h-4 mr-2" /> Activate {product.title}
            </Button>
            <Link
              to="/total-domination"
              className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-foreground"
              data-testid="link-back-total-domination"
            >
              <Store className="w-4 h-4" /> Back to Total Domination
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
